import {
  DAY_INDEX_ENUM,
  type DayIndex,
  type PlatformKey,
} from "@/constants/enums";
import type { GroupedShifts, Shift, ShiftWindow } from "@/types";

const DAY_ORDER: DayIndex[] = [
  DAY_INDEX_ENUM.SUN,
  DAY_INDEX_ENUM.MON,
  DAY_INDEX_ENUM.TUE,
  DAY_INDEX_ENUM.WED,
  DAY_INDEX_ENUM.THU,
  DAY_INDEX_ENUM.FRI,
  DAY_INDEX_ENUM.SAT,
];

/** The restaurant's current day key, for highlighting "today" in the UI. */
export const getCurrentDayIndex = (
  utcOffsetHours?: number,
  now: Date = new Date(),
): DayIndex => zonedParts(now, utcOffsetHours).dayIndex;

/** True when the tenant has manually paused ordering for this platform. */
export const isOrderingStopped = (
  stopList: boolean | PlatformKey[] | undefined,
  platformKey: PlatformKey,
  isStaff: boolean,
): boolean => {
  if (isStaff) return false;
  if (!stopList) return false;
  if (Array.isArray(stopList)) return stopList.includes(platformKey);
  return Boolean(stopList);
};

/**
 * Monday-first, and every day present. The original seeds each platform's day
 * map with all seven keys in this order (module 60831 `Kq`), which is what
 * makes the Store Hours screen list Monday→Sunday and render "Closed" for a day
 * with no shifts — it iterates `Object.entries`, so both the order and the
 * empty entries come from here rather than from the screen.
 */
const WEEK_ORDER: DayIndex[] = [
  DAY_INDEX_ENUM.MON,
  DAY_INDEX_ENUM.TUE,
  DAY_INDEX_ENUM.WED,
  DAY_INDEX_ENUM.THU,
  DAY_INDEX_ENUM.FRI,
  DAY_INDEX_ENUM.SAT,
  DAY_INDEX_ENUM.SUN,
];

const emptyWeek = (): Record<DayIndex, ShiftWindow[]> =>
  WEEK_ORDER.reduce(
    (week, day) => {
      week[day] = [];
      return week;
    },
    {} as Record<DayIndex, ShiftWindow[]>,
  );

/**
 * Turns the flat shift list returned by the API into
 * `{ [platformKey]: { [dayIndex]: ShiftWindow[] } }`.
 *
 * A platform key only appears once at least one shift names it — a platform
 * with no trading hours at all must stay `undefined`, because that is what
 * `useStoreStatus` and the Store Hours tab list both test for.
 */
export const groupShiftsByPlatform = (shifts: Shift[] = []): GroupedShifts => {
  const grouped: GroupedShifts = {};

  shifts
    .filter((shift) => shift?.active !== false)
    .forEach((shift) => {
      (shift.included_platforms ?? []).forEach((platform) => {
        const days = (grouped[platform] ??= emptyWeek());
        const windows = (days[shift.day_index] ??= []);
        windows.push({
          start_time: shift.start_time,
          end_time: shift.end_time,
          name: shift.name,
        });
      });
    });

  return grouped;
};

/**
 * `nextDayMergedByPlatform` — the second half of the original's `Kq` return
 * value, and the flag the Store Hours screen uses to decide when "today" rolls
 * over.
 *
 * A tenant that trades past midnight is still on "Saturday" at 1am on Sunday,
 * both to its staff and in its own shift data. The flag marks the platforms
 * where that is true, and the two helpers below consume it: until 6am, such a
 * platform reports the previous day and a time past the 24-hour mark.
 *
 * The two conditions are the original's: a shift whose `end_time` runs past 24h
 * while it starts before, or a shift that lies wholly inside the first six
 * hours of a day (which the original additionally re-files under the previous
 * day — see the note in RECOVERY_PLAN.md, that part is not reproduced).
 */
const SIX_HOURS_MS = 21600000;

export const nextDayMergedByPlatform = (
  shifts: Shift[] = [],
): Record<string, boolean> => {
  const merged: Record<string, boolean> = {};

  shifts
    .filter((shift) => shift?.active !== false)
    .forEach(({ included_platforms, start_time, end_time }) => {
      const crossesMidnight = end_time > DAY_MS && start_time < DAY_MS;
      const whollyEarly = start_time <= SIX_HOURS_MS && end_time <= SIX_HOURS_MS;
      if (!crossesMidnight && !whollyEarly) return;
      (included_platforms ?? []).forEach((platform) => {
        merged[platform] = true;
      });
    });

  return merged;
};

/**
 * `Ze` — milliseconds since midnight, pushed past 24h during the small hours on
 * a platform that trades overnight, so it can be compared directly against a
 * shift whose `end_time` is 25h.
 */
export const currentMsSinceMidnight = (
  nextDayMerged = false,
  now: Date = new Date(),
): number => {
  const ms = msSinceMidnight(now);
  return ms <= SIX_HOURS_MS && nextDayMerged ? ms + DAY_MS : ms;
};

/** `CS` — today's key, rolled back to yesterday during those same small hours. */
export const currentDayIndexMerged = (
  nextDayMerged = false,
  now: Date = new Date(),
): DayIndex => {
  let day = now.getDay();
  if (nextDayMerged && msSinceMidnight(now) <= SIX_HOURS_MS) {
    day = (day - 1 + 7) % 7;
  }
  return (DAY_ORDER[day] ?? DAY_INDEX_ENUM.SUN) as DayIndex;
};

/* ----------------------------- shift evaluation ---------------------------- */

const DAY_MS = 86400000;

/** Milliseconds since midnight for a given moment. */
export const msSinceMidnight = (now: Date): number =>
  now.getHours() * 3600000 +
  now.getMinutes() * 60000 +
  now.getSeconds() * 1000 +
  now.getMilliseconds();

/**
 * The wall-clock day and time *at the restaurant*.
 *
 * Trading hours belong to the shop, not to whoever is looking at the page. A
 * customer in London reading a Pakistani restaurant's hours must see them
 * evaluated in Islamabad time, or the store status is simply wrong — and the
 * same applies to anyone travelling, or to a phone with a mis-set clock.
 *
 * `utcOffsetHours` comes from the tenant's `operational_time_zone_offset_gmt`.
 * When it is absent the browser's own timezone is used, which is the best
 * available guess.
 */
export const zonedParts = (
  now: Date,
  utcOffsetHours?: number,
): { dayIndex: DayIndex; msSinceMidnight: number } => {
  if (utcOffsetHours == null) {
    return {
      dayIndex: DAY_ORDER[now.getDay()] as DayIndex,
      msSinceMidnight: msSinceMidnight(now),
    };
  }

  // Shift the instant into the restaurant's zone, then read it as UTC — this
  // is independent of whatever zone the browser happens to be in.
  const shifted = new Date(now.getTime() + utcOffsetHours * 3600000);
  return {
    dayIndex: DAY_ORDER[shifted.getUTCDay()] as DayIndex,
    msSinceMidnight:
      shifted.getUTCHours() * 3600000 +
      shifted.getUTCMinutes() * 60000 +
      shifted.getUTCSeconds() * 1000 +
      shifted.getUTCMilliseconds(),
  };
};

const shiftDayKey = (dayIndex: DayIndex, offsetDays: number): DayIndex => {
  const index = (((DAY_ORDER.indexOf(dayIndex) + offsetDays) % 7) + 7) % 7;
  return DAY_ORDER[index] as DayIndex;
};

const covers = (shift: ShiftWindow, atMs: number): boolean =>
  shift.start_time <= atMs && shift.end_time >= atMs;

/**
 * Decides whether a set of trading hours is open, closed, or on a break right
 * now.
 *
 * A shift is stored against the day it *starts*, and `end_time` may run past
 * 24h to express "closes at 2am tomorrow" — so being inside a shift means
 * either today's hours cover the current time, or yesterday's hours cover it
 * once shifted forward by a day.
 *
 * The earlier version added a day to the lookup instead of subtracting one, and
 * applied the 24h offset all day rather than only for the overnight tail. For a
 * tenant open 09:00–02:00 that reported "Closed" at lunchtime and right through
 * the dinner rush — most of the trading day.
 */
export interface ShiftOptions {
  /**
   * When false, ordering stops `prepMinutes` before closing so the kitchen can
   * finish the last order. `web_allow_ordering_till_closing_time` is false for
   * this tenant, yet the app happily took orders at 23:58 for a 00:00 close.
   */
  allowOrderingTillClosingTime?: boolean;
  /** `default_prep_minutes` — how long the kitchen needs. */
  prepMinutes?: number;
}

/** Milliseconds to trim from the end of each shift before it stops accepting. */
export const orderingCutoffMs = ({
  allowOrderingTillClosingTime,
  prepMinutes,
}: ShiftOptions = {}): number =>
  allowOrderingTillClosingTime === false
    ? Math.max(0, prepMinutes ?? 0) * 60000
    : 0;

export const evaluateShifts = (
  days: Partial<Record<DayIndex, ShiftWindow[]>> | undefined,
  now: Date = new Date(),
  utcOffsetHours?: number,
  options: ShiftOptions = {},
): "OPEN" | "CLOSED" | "BREAK" => {
  if (!days) return "CLOSED";

  const { dayIndex, msSinceMidnight: nowMs } = zonedParts(now, utcOffsetHours);

  // Shrink each window by the prep time, so the last order placed can still be
  // cooked before the shutter. A shift shorter than the prep time collapses to
  // its start rather than inverting.
  const cutoff = orderingCutoffMs(options);
  const shrink = (shift: ShiftWindow): ShiftWindow =>
    cutoff > 0
      ? {
          ...shift,
          end_time: Math.max(shift.start_time, shift.end_time - cutoff),
        }
      : shift;

  const today = (days[dayIndex] ?? []).map(shrink);
  const yesterday = (days[shiftDayKey(dayIndex, -1)] ?? []).map(shrink);

  const openNow =
    today.some((shift) => covers(shift, nowMs)) ||
    // Yesterday's overnight tail, e.g. a 09:00–26:00 shift seen at 01:00.
    yesterday.some((shift) => covers(shift, nowMs + DAY_MS));

  if (openNow) return "OPEN";

  // Closed, but between two of today's sittings rather than done for the day.
  const finished = today.some((shift) => shift.end_time <= nowMs);
  const upcoming = today.some((shift) => shift.start_time > nowMs);
  return finished && upcoming ? "BREAK" : "CLOSED";
};
